import assistantStore, { setUnreadAnswer } from "@/stores/assistantStore";
import { useStore } from "@tanstack/react-store";
import { useEffect, useRef } from "react";

/**
 * « Réponse à lire » : an answer that arrives while its thread is not on screen waits to be seen — the
 * assistant closed (it stays mounted, hidden), the tab hidden, or the history covering the thread (narrow
 * screen, `threadHidden`). It is seen as soon as the thread shows: the assistant open in a visible tab,
 * the history no longer over it. One turn at a time: the answer is always the conversation shown's.
 * Returns whether one waits (the assistant's button, and the conversation in the history, say so).
 */
const useUnreadAnswer = (
    isSendingMessage: boolean,
    threadHidden = false,
): boolean => {
    const unreadAnswer = useStore(assistantStore, (s) => s.unreadAnswer);
    const isOpen = useStore(assistantStore, (s) => s.isOpen);
    const wasSending = useRef(isSendingMessage);
    const hidden = useRef(threadHidden);
    hidden.current = threadHidden;
    const isSeen = () =>
        assistantStore.state.isOpen &&
        document.visibilityState === "visible" &&
        !hidden.current;

    useEffect(() => {
        if (wasSending.current && !isSendingMessage && !isSeen()) {
            setUnreadAnswer(true);
        }
        wasSending.current = isSendingMessage;
    }, [isSendingMessage]);

    useEffect(() => {
        const markSeen = () => {
            if (isSeen()) setUnreadAnswer(false);
        };
        markSeen();
        document.addEventListener("visibilitychange", markSeen);
        return () => document.removeEventListener("visibilitychange", markSeen);
    }, [isOpen, threadHidden]);

    return unreadAnswer;
};

export default useUnreadAnswer;
